import React, { useState, useEffect } from "react";
import { X, Save, Link, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import Label from "../../components/ui/label";
import { Textarea } from "../../components/ui/Textarea";
import { Card } from "../../components/ui/card";
import {
  MenuLink,
  MenuLinkCreateDto,
  MenuLinkUpdateDto,
  MenuLinkVisibilityRef,
  MenuLinkLocationRef
} from "../../modules/shared/model/menu-link.interfaces";
import {
  MenuLinkVisibilityEnum,
  MenuLinkLocationEnum
} from "../../modules/shared/model/menu-link.enum";

interface MenuLinkModalProps {
  item: MenuLink | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: MenuLinkCreateDto | MenuLinkUpdateDto) => void;
  title: string;
}

const MenuLinkModal: React.FC<MenuLinkModalProps> = ({
  item,
  isOpen,
  onClose,
  onSave,
  title
}) => {
  const [formData, setFormData] = useState({
    title: "",
    url: "",
    order: 1,
    visibility: MenuLinkVisibilityEnum.All,
    location: MenuLinkLocationEnum.Header
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || "",
        url: item.url || "",
        order: item.order || 1,
        visibility: Number(item.visibility?.id) || MenuLinkVisibilityEnum.All,
        location: Number(item.location?.id) || MenuLinkLocationEnum.Header
      });
    } else {
      setFormData({
        title: "",
        url: "",
        order: 1,
        visibility: MenuLinkVisibilityEnum.All,
        location: MenuLinkLocationEnum.Header
      });
    }
    setErrors({});
  }, [item]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.url.trim()) {
      newErrors.url = "URL is required";
    } else if (!isValidUrl(formData.url)) {
      newErrors.url = "Please enter a valid URL";
    }

    if (formData.order < 1) {
      newErrors.order = "Order must be at least 1";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      // Check if it's a relative URL
      return url.startsWith("/") || url.startsWith("#");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const data: MenuLinkCreateDto | MenuLinkUpdateDto = {
        title: formData.title.trim(),
        url: formData.url.trim(),
        order: formData.order,
        visibility: { id: formData.visibility },
        location: { id: formData.location }
      };

      await onSave(data);
      onClose();
    } catch (error) {
      console.error("Error saving menu link:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const getVisibilityLabel = (visibilityId: number) => {
    return Object.keys(MenuLinkVisibilityEnum).find(key => 
      MenuLinkVisibilityEnum[key as keyof typeof MenuLinkVisibilityEnum] === visibilityId
    ) || "All";
  };

  const getLocationLabel = (locationId: number) => {
    return Object.keys(MenuLinkLocationEnum).find(key => 
      MenuLinkLocationEnum[key as keyof typeof MenuLinkLocationEnum] === locationId
    ) || "Header";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Link className="h-5 w-5 text-blue-600" />
              {title}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="Enter menu item title"
                    className={errors.title ? "border-red-500" : ""}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-600">{errors.title}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url">URL *</Label>
                  <Input
                    id="url"
                    value={formData.url}
                    onChange={(e) => handleInputChange("url", e.target.value)}
                    placeholder="Enter URL (e.g., /about, https://example.com)"
                    className={errors.url ? "border-red-500" : ""}
                  />
                  {errors.url && (
                    <p className="text-sm text-red-600">{errors.url}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Can be a relative path (/about) or full URL (https://example.com)
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  min="1"
                  value={formData.order}
                  onChange={(e) => handleInputChange("order", parseInt(e.target.value) || 1)}
                  className={errors.order ? "border-red-500" : ""}
                />
                {errors.order && (
                  <p className="text-sm text-red-600">{errors.order}</p>
                )}
                <p className="text-xs text-gray-500">
                  Lower numbers appear first in the menu
                </p>
              </div>
            </div>

            {/* Visibility and Location Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Display Settings</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="visibility">Visibility</Label>
                  <select
                    id="visibility"
                    value={formData.visibility.toString()}
                    onChange={(e) => handleInputChange("visibility", parseInt(e.target.value))}
                    className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value={MenuLinkVisibilityEnum.All.toString()}>All Users</option>
                    <option value={MenuLinkVisibilityEnum.Guest.toString()}>Guest Only</option>
                    <option value={MenuLinkVisibilityEnum.Registered.toString()}>Registered Users</option>
                    <option value={MenuLinkVisibilityEnum.Professional.toString()}>Professionals Only</option>
                    <option value={MenuLinkVisibilityEnum.Customer.toString()}>Customers Only</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Who can see this menu item
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <select
                    id="location"
                    value={formData.location.toString()}
                    onChange={(e) => handleInputChange("location", parseInt(e.target.value))}
                    className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value={MenuLinkLocationEnum.Header.toString()}>Header Navigation</option>
                    <option value={MenuLinkLocationEnum.Footer.toString()}>Footer Navigation</option>
                    <option value={MenuLinkLocationEnum.Sidebar.toString()}>Sidebar Navigation</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Where this menu item will appear
                  </p>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Preview</h3>
              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <Link className="h-4 w-4 text-blue-600" />
                  <span className="font-medium">{formData.title || "Menu Item Title"}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getLocationLabel(formData.location)}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {getVisibilityLabel(formData.visibility)}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  URL: {formData.url || "/example"}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Order: {formData.order}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? "Saving..." : item ? "Update Menu Item" : "Create Menu Item"}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default MenuLinkModal;
